import { Bell, User, Menu, X, LogOut, ShoppingCart } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import i18n from 'i18next';
import { useMediaQuery, Button, Box } from "@mui/material";
import { usePTagForm } from "../contexts/PTagFormContext";
import NotifyContainer from "../pages/NotifyContainer";
import tokenManager from "../services/tokenManager";
import SvgPaw from "./SVG/svgPaw";

const LOGGED_OUT_FLAG = "ptag_logged_out";

const NAV_ICONS = {
  manage: { static: "/icons/head-manage.png", animated: "/icons/head-manage.gif" },
  finder: { static: "/icons/head-finder.png", animated: "/icons/head-finder.gif" },
  shop: { static: "/icons/head-shop-2.png", animated: "/icons/head-shop-2.gif" },
  lost: { static: "/icons/head-lost.png", animated: "/icons/head-lost.gif" },
  adopt: { static: "/icons/head-adopt-2.png", animated: "/icons/head-adopt-2.gif" },
  admin: { static: "/icons/head-admin.png", animated: "/icons/head-admin.gif" },
};

function NavIcon({ icon, alt, className, style, isAnimated }) {
  const sources = NAV_ICONS[icon];

  return (
    <img
      src={isAnimated ? sources.animated : sources.static}
      alt={alt}
      className={className}
      style={style}
    />
  );
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showTranslationModal, setshowTranslationModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('zh');
  const [showShopItems, setShowShopItems] = useState(false);
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const shopDropdownTimer = useRef(null);
  const { formsAdded, orderConfirmed, removeAllForms } = usePTagForm();
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setUser(true);
    } else {
      setUser(false);
    }
  }, []);

  // Get notification count from cookies
  useEffect(() => {
    const updateNotificationCount = () => {
      const notificationsCookie = Cookies.get("notifications");
      if (notificationsCookie) {
        try {
          const count = JSON.parse(notificationsCookie);
          setUnreadNotificationCount(count);
        } catch (error) {
          console.error("Error parsing notifications cookie:", error);
          setUnreadNotificationCount(0);
        }
      } else {
        setUnreadNotificationCount(0);
      }
    };

    // Update on mount
    updateNotificationCount();

    // Check for changes periodically (optional)
    const interval = setInterval(updateNotificationCount, 1000);

    return () => clearInterval(interval);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const getUserId = () => {
    const id = Cookies.get("id");
    return id;
  };

  const getUserRole = () => {
    const role = Cookies.get("role");
    return role;
  };



  // Fetch user role
  useEffect(() => {
    const token = Cookies.get("token");
    const role = getUserRole();
    if (role === 'admin' && token) {
      setAdmin(true);
    } else {
      setAdmin(false);
    }
  }, []);

  // Sync selectedLanguage with i18n language
  useEffect(() => {
    const currentLanguage = i18n.language;
    setSelectedLanguage(currentLanguage);
  }, []);

  const handleNavClick = () => {
    if (orderConfirmed) {
      removeAllForms();
    }
  };

  const handleShopMouseEnter = () => {
    if (shopDropdownTimer.current) {
      clearTimeout(shopDropdownTimer.current);
    }
    setShowShopDropdown(true);
  };

  const handleShopMouseLeave = () => {
    setShowShopDropdown(false);
  };

  const handleLogout = () => {
    // Clear tokens from memory (primary storage)
    tokenManager.clear();
    localStorage.setItem(LOGGED_OUT_FLAG, "true");

    // Clear cookies (backward compatibility)
    Cookies.remove("token");
    Cookies.remove("id");
    Cookies.remove("userRole");
    Cookies.remove("refreshToken");
    Cookies.remove("refreshToken", { path: "/" });
    Cookies.remove("refreshToken", { path: "/auth/tokens/refresh" });
    Cookies.remove("refreshToken", { path: "/development/auth/tokens/refresh" });
    Cookies.remove("notifications");
    Cookies.remove("role");

    // Update UI state
    setUser(false);
    setShowLogoutModal(false);

    // Redirect to login
    navigate("/login");
  };

  const isMobile = useMediaQuery("(max-width:850px)");

  return (
    <header
      className="bg-white border-gray-200 py-3 md:py-0 fixed top-0 left-0 right-0 z-50 shadow-sm"
    >
      <div style={{ display: 'flex', justifyContent: "space-between", alignItems: 'center', padding: isMobile ? '0 16px' : '0 80px', }}>
        <Link to="/" onClick={handleNavClick} className="text-2xl md:text-3xl font-bold position-relative">
          PTag
        </Link>
        <div className="hidden md:flex">
          {user && (
            <Link
              to="/pet-management"
              onClick={handleNavClick}
              onMouseEnter={() => setHoveredNavItem("manage")}
              onMouseLeave={() => setHoveredNavItem(null)}
              className="text-sm hover:text-white hover:bg-black p-6 group flex flex-col items-center gap-1.5"
            >
              <NavIcon
                icon="manage"
                alt="Manage"
                className="group-hover:invert"
                isAnimated={hoveredNavItem === "manage"}
                style={{
                  width: "25px",
                  height: "auto",
                }}
              />
              <div>{t('PetManagementHeader')}</div>
            </Link>
          )}
          <Link
            to="/landing"
            onClick={handleNavClick}
            onMouseEnter={() => setHoveredNavItem("finder")}
            onMouseLeave={() => setHoveredNavItem(null)}
            className="text-sm hover:text-white hover:bg-black p-6 group flex flex-col items-center gap-1.5"
          >
            <NavIcon
              icon="finder"
              alt="Track"
              className="group-hover:invert"
              isAnimated={hoveredNavItem === "finder"}
              style={{
                width: "25px",
                height: "auto",
              }}
            />
            {t('Track')}
          </Link>
          <div
            className="relative hover:text-white hover:bg-black"
            onMouseEnter={() => {
              handleShopMouseEnter();
              setHoveredNavItem("shop");
            }}
            onMouseLeave={() => {
              handleShopMouseLeave();
              setHoveredNavItem(null);
            }}
          >
            <button
              onClick={handleNavClick}
              className="text-sm p-6 group cursor-pointer flex flex-col items-center gap-1.5"
            >
              <NavIcon
                icon="shop"
                alt="Shop"
                className={`${showShopDropdown ? 'invert' : 'group-hover:invert'} rotate-180`}
                isAnimated={hoveredNavItem === "shop"}
                style={{
                  width: "25px",
                  height: "auto",
                }}
              />
              {t('PetTags')}
            </button>

            {/* Shop Dropdown */}
            <div
              className={`absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-out ${showShopDropdown
                ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 -translate-y-4 pointer-events-none'
                }`}
              style={{
                top: '100%',
                paddingTop: '8px',
                zIndex: 100,
              }}
            >
              <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 flex gap-6">
                {/* PTag Option */}
                <Link
                  to="/ptag"
                  onClick={() => { handleNavClick(); setShowShopDropdown(false); }}
                  className="flex flex-col items-center gap-2 group transition-all duration-200"
                  style={{ width: '120px', cursor: 'pointer' }}
                >
                  <div className="relative overflow-hidden rounded-full transition-all duration-300 group-hover:scale-110" style={{ cursor: 'pointer' }}>
                    <img
                      src="/icons/ptag-2.png"
                      alt="PTag"
                      className="w-24 h-24 object-cover rounded-full border-2 border-gray-200 group-hover:border-yellow-400 transition-all duration-300"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-yellow-600 transition-colors duration-200" style={{ cursor: 'pointer' }}>
                    PTag
                  </span>
                </Link>

                {/* PTag Air Option */}
                <Link
                  to="/ptag-air"
                  onClick={() => { handleNavClick(); setShowShopDropdown(false); }}
                  className="flex flex-col items-center gap-2 group transition-all duration-200"
                  style={{ width: '120px', cursor: 'pointer' }}
                >
                  <div className="relative overflow-hidden rounded-full transition-all duration-300 group-hover:scale-110" style={{ cursor: 'pointer' }}>
                    <img
                      src="/icons/ptagair-2.png"
                      alt="PTag Air"
                      className="w-24 h-24 object-cover rounded-full border-2 border-gray-200 group-hover:border-yellow-400 transition-all duration-300"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-yellow-600 transition-colors duration-200" style={{ cursor: 'pointer' }}>
                    PTag Air
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <Link
            to="/all_lost_pets"
            onClick={handleNavClick}
            onMouseEnter={() => setHoveredNavItem("lost")}
            onMouseLeave={() => setHoveredNavItem(null)}
            className="text-sm hover:text-white hover:bg-black p-6 group flex flex-col items-center gap-1.5"
          >
            <NavIcon
              icon="lost"
              alt="Lost"
              className="group-hover:invert"
              isAnimated={hoveredNavItem === "lost"}
              style={{
                width: "25px",
                height: "auto",
              }}
            />
            {t('Registry')}
          </Link>
          <Link
            to="/adoption"
            onClick={handleNavClick}
            onMouseEnter={() => setHoveredNavItem("adopt")}
            onMouseLeave={() => setHoveredNavItem(null)}
            className="text-sm hover:text-white hover:bg-black p-5 group flex flex-col items-center"
          >
            <NavIcon
              icon="adopt"
              alt="Adopt"
              className="group-hover:invert"
              isAnimated={hoveredNavItem === "adopt"}
              style={{
                width: "40px",
                height: "auto",
              }}
            />
            {t('Adoption')}
          </Link>
          {admin && (
            <Link
              to="/check_tag"
              onClick={handleNavClick}
              onMouseEnter={() => setHoveredNavItem("admin")}
              onMouseLeave={() => setHoveredNavItem(null)}
              className="text-sm hover:text-white hover:bg-black p-6 group flex flex-col items-center gap-1.5"
            >
              <NavIcon
                icon="admin"
                alt="Adopt"
                className="group-hover:invert"
                isAnimated={hoveredNavItem === "admin"}
                style={{
                  width: "25px",
                  height: "auto",
                }}
              />
              {t('Check')}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative my-2">
            <button
              type="button"
              className="p-2 hover:bg-gray-100 rounded-md transition-colors duration-200 cursor-pointer flex items-center justify-center gap-1"
              onClick={() => setshowTranslationModal(prev => !prev)}
              aria-expanded={showTranslationModal}
              aria-controls="languageMenu"
              style={{
                minWidth: 40,
                fontWeight: 400,
                fontSize: '14px',
                border: '1px solid black',
              }}
            >
              {selectedLanguage === 'zh' ? 'ZH' : 'EN'}
              {showTranslationModal ? (
                <ChevronUp className="h-3 w-3" strokeWidth={2} />
              ) : (
                <ChevronDown className="h-3 w-3" strokeWidth={2} />
              )}
            </button>

            {showTranslationModal && (
              <div id="languageMenu"
                style={{ position: "absolute", minWidth: 50, top: "calc(100% + 2px)", right: 0, backgroundColor: "#000000", border: "1px solid #D1D1D1", borderRadius: 5, display: 'flex', flexDirection: 'column', zIndex: 100 }}>
                <Button
                  sx={{
                    cursor: 'pointer',
                    color: 'white',
                    fontWeight: 400,
                    minWidth: 0,
                    border: '1px solid transparent',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#FFF',
                      color: 'black',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      border: '1px solid black',
                      '& .language-paw': {
                        fill: 'black'
                      }
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); setSelectedLanguage('zh'); i18n.changeLanguage('zh'); setshowTranslationModal(false);
                  }}>
                  <div style={{ paddingRight: '2px' }}>ZH</div>
                  {selectedLanguage === 'zh' && <SvgPaw className="language-paw" />}
                </Button>
                <Button
                  sx={{
                    cursor: 'pointer',
                    color: 'white',
                    fontWeight: 400,
                    minWidth: 0,
                    border: '1px solid transparent',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#FFF',
                      color: 'black',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      border: '1px solid black',
                      '& .language-paw': {
                        fill: 'black'
                      }
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); setSelectedLanguage('en'); i18n.changeLanguage('en'); setshowTranslationModal(false);
                  }}>
                  <div style={{ paddingRight: '2px' }}>EN</div>
                  {selectedLanguage === 'en' && <SvgPaw className="language-paw" />}
                </Button>
              </div>
            )}
          </div>
          <button
            className={`text-sm ${isMenuOpen && isMobile ? 'text-black bg-white border-black' : 'text-white bg-black border-white'} hover:text-black hover:bg-white hover:border-2
            hover:border-black cursor-pointer border-2 font-bold
            transition-all duration-200 px-2 py-1.5 rounded`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "關閉選單" : "打開選單"}
          >
            <div style={{ position: 'relative', width: '24px', height: '24px' }}>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  transform: isMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              >
                <Menu className="h-6 w-6" strokeWidth={2} />
                {user && unreadNotificationCount > 0 && !open && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: '8px',
                      height: '8px',
                      background: '#facc15',
                      borderRadius: '9999px',
                      border: '1px solid #fff',
                      zIndex: 10,
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  transform: isMenuOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  opacity: isMenuOpen ? 1 : 0,
                }}
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </div>
            </div>
          </button>
        </div>

      </div>

      {/* 手機版彈出菜單 */}
      {isMenuOpen && (
        <>
          <div
            className="fixed bg-black/40 z-40"
            style={{ top: isMobile ? '80px' : '100px', left: 0, right: 0, bottom: 0 }}
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 z-50 bg-white border-t border-black shadow-lg">
            <div style={{ padding: isMobile ? '10px 16px' : '20px 80px', }}>
              <div className="flex flex-col space-y-4">
                {user && isMobile && (
                  <Link
                    to="/pet-management"
                    style={{ fontSize: '20px', }}
                    className="text-left py-2 mx-4 border-b border-black text-black transition-colors duration-200"
                    onClick={() => { setIsMenuOpen(false); handleNavClick(); }}
                  >
                    {t('PetManagementHeader')}
                  </Link>
                )}
                {isMobile && (
                  <Link
                    to="/landing"
                    style={{ fontSize: '20px', }}
                    className="text-left py-2 mx-4 border-b border-black text-black transition-colors duration-200"
                    onClick={() => { setIsMenuOpen(false); handleNavClick(); }}
                  >
                    {t('Track')}
                  </Link>
                )}
                {isMobile && (
                  <div className="py-2 mx-4 border-b border-black">
                    <button
                      onClick={() => setShowShopItems(!showShopItems)}
                      style={{ fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      className="w-full flex items-center justify-end gap-2 text-left text-black transition-colors duration-200"
                    >
                      <span>{t('Shop')}</span>
                      {showShopItems ? (
                        <ChevronUp className="h-5 w-5" strokeWidth={4} stroke="black" />
                      ) : (
                        <ChevronDown className="h-5 w-5" strokeWidth={4} stroke="black" />
                      )}
                    </button>
                    {showShopItems && (
                      <div className="m-2 flex gap-2">
                        <Box
                          component="button"
                          type="button"
                          onClick={() => {
                            navigate("/ptag");
                          }}
                          sx={{ flex: 1, minHeight: 25, py:1, borderRadius: "5px", border: "2px solid #FFB60C", color: "black", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                        >
                          <img src="/icons/ptag-2.png" alt="PTag" style={{ width: 20, height: 20 }} />
                          PTag
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => {
                            navigate("/ptag-air");
                          }}
                          sx={{ flex: 1, minHeight: 25, py:1,  borderRadius: "5px", border: "2px solid #FFB60C", color: "black", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                        >
                          <img src="/icons/ptagair-2.png" alt="PTagAir" style={{ width: 20, height: 20 }} />
                          PTag Air
                        </Box>
                      </div>
                    )}
                  </div>
                )}
                {isMobile && (
                  <Link
                    to="/all_lost_pets"
                    style={{ fontSize: '20px', }}
                    className="text-left py-2 mx-4 border-b border-black text-black transition-colors duration-200"
                    onClick={() => { setIsMenuOpen(false); handleNavClick(); }}
                  >
                    {t('Registry')}
                  </Link>
                )}
                {isMobile && (
                  <Link
                    to="/adoption"
                    style={{ fontSize: '20px', }}
                    className="text-left py-2 mx-4 border-b border-black text-black transition-colors duration-200"
                    onClick={() => { setIsMenuOpen(false); handleNavClick(); }}
                  >
                    {t('Adoption')}
                  </Link>
                )}
                {admin && isMobile && (
                  <Link
                    to="/check_tag"
                    style={{ fontSize: '20px', }}
                    className="text-left py-2 mx-4 border-b border-black text-black transition-colors duration-200"
                    onClick={() => { setIsMenuOpen(false); handleNavClick(); }}
                  >
                    {t('Check')}
                  </Link>
                )}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "20px", }}>
                  <button
                    className="p-1 hover:bg-black rounded-md transition-colors duration-200 relative"
                  >
                    <Link to="/shopping_cart" onClick={handleNavClick}>
                      <ShoppingCart className="h-8 w-8" strokeWidth={1.5} stroke="#FFB60C" />
                      {formsAdded > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            background: "#facc15",
                            color: "#000",
                            borderRadius: "9999px",
                            fontSize: "8px",
                            fontWeight: "bold",
                            width: "15px",
                            height: "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #fff",
                            zIndex: 10,
                          }}
                        >
                          {formsAdded}
                        </span>
                      )}
                    </Link>
                  </button>

                  {user && (
                    <button
                      className="p-1 hover:bg-black rounded-md cursor-pointer transition-colors duration-200 relative"
                      onClick={() => navigate("/notification")}
                    >
                      <Bell className="h-8 w-8" strokeWidth={1.5} stroke="#FFB60C" />
                      {unreadNotificationCount > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: -2,
                            right: -2,
                            background: "#facc15",
                            color: "#000",
                            borderRadius: "9999px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            minWidth: "18px",
                            height: "18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #fff",
                            padding: "0 4px",
                            zIndex: 10,
                          }}
                        >
                          {unreadNotificationCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button className="p-1 hover:bg-black rounded-md transition-colors duration-200">
                    {user ? (
                      <Link to={`/account/${getUserId()}`}>
                        <User className="h-8 w-8" strokeWidth={1.5} stroke="#FFB60C" />
                      </Link>
                    ) : (
                      <Link to="/login">
                        <User className="h-8 w-8" strokeWidth={1.5} stroke="#FFB60C" />
                      </Link>
                    )}
                  </button>
                  {user && (
                    <button
                      className="p-1 hover:bg-black cursor-pointer rounded-md transition-colors duration-200"
                      onClick={() => setShowLogoutModal(true)}
                    >
                      <LogOut className="h-8 w-8" strokeWidth={1.5} stroke="#FFB60C" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* NotifyContainer Panel */}
      <NotifyContainer open={open} onClose={() => setOpen(false)} />

      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-xs w-full flex flex-col items-center">
            <div className="text-lg font-semibold mb-6">{t("logout")}</div>
            <div className="flex w-full gap-4">
              <button
                className="flex-1 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold"
                onClick={() => setShowLogoutModal(false)}
              >
                {t("cancel")}
              </button>
              <button
                className="flex-1 py-2 rounded bg-yellow-400 hover:bg-yellow-500 text-black font-bold"
                onClick={handleLogout}
              >
                {t("logoutConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
